import dayjs, { Dayjs } from 'dayjs';

import { Organization } from 'models/entities/organization';
import { Company, CompanyData } from 'models/entities/company';
import { StoreCollection, StoreCollectionData, Store } from 'models/entities/store';

import { CreateGql, UpdateGql, DeleteGql } from './gql';

type Data = {
  id?: string;
  type?: string;
  parentId?: string;
  ownerId?: string;
  organizationId: string;
  company?: CompanyData | Company;
  name?: string;
  stores?: StoreCollectionData;
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

type DataToEdit = {
  company?: Company;
  name?: string;
};

type DataToSave = {
  type: string;
  organizationId: string;
  companyId: string;
  name: string;
};

class Franchisee implements Organization {

  readonly id: string;
  readonly type: string;
  readonly parentId?: string;
  readonly ownerId?: string;
  readonly organizationId: string;
  readonly company?: Company;
  readonly name: string;
  readonly stores: StoreCollection;
  readonly userCount: number;
  readonly createdAt?: Dayjs;
  readonly updatedAt?: Dayjs;
  readonly organizationIds: Set<string>;

  constructor(data: Data) {
    this.id = data.id ?? '';
    this.type = 'Franchisee';
    this.parentId = data.parentId ?? '';
    this.ownerId = data.ownerId ?? '';
    this.organizationId = data.organizationId;
    this.company = data.company ? data.company instanceof Company ? data.company : new Company(data.company) : undefined;
    this.name = data.name ?? '';
    this.stores = new StoreCollection(this.id, data.stores);
    this.userCount = data.userCount ?? 0;
    this.createdAt = data.createdAt ? dayjs(data.createdAt) : undefined;
    this.updatedAt = data.updatedAt ? dayjs(data.updatedAt) : undefined;
    this.organizationIds = this.getOrganizationIds(this.stores);
  }

  edit(data: DataToEdit): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, ...data });
  }

  validate(): boolean {
    if (!this.company) return false;
    if (!this.name) return false;
    return true;
  }

  async save(): Promise<Franchisee> {
    if (!this.validate()) throw new Error('invalid cache object');
    return this.id ? this.update() : this.create();
  }

  private async create(): Promise<Franchisee> {
    const gql = await new CreateGql().fetch({ organizationId: this.organizationId, input: this.getDataToSave() });
    if (!gql.document) throw new Error('invalid document');
    return new Franchisee(gql.document);
  }

  private async update(): Promise<Franchisee> {
    if (!this.id) throw new Error('invalid relationship');
    const gql = await new UpdateGql().fetch({ id: this.id, input: this.getDataToSave() });
    if (!gql.document) throw new Error('invalid document');
    return new Franchisee(gql.document);
  }

  private getDataToSave(): DataToSave {
    const { type, organizationId, company, name } = this;
    return { type, organizationId, companyId: company!.id!, name };
  }

  async delete(): Promise<Franchisee> {
    if (!this.id) throw new Error('invalid relationship');
    const gql = await new DeleteGql().fetch({ id: this.id });
    if (!gql.document) throw new Error('invalid document');
    return new Franchisee(gql.document);
  }

  has(organizationId: string): boolean {
    return this.organizationIds.has(organizationId);
  }

  applyStore(store: Store): this {
    if (store.organizationId !== this.id) return this;
    const stores = this.stores.apply(store);
    const organizationIds = this.getOrganizationIds(stores);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, stores, organizationIds });
  }

  removeStore(store: Store): this {
    if (store.organizationId !== this.id) return this;
    const stores = this.stores.remove(store);
    const organizationIds = this.getOrganizationIds(stores);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, stores, organizationIds });
  }

  getStores(): Store[] {
    return this.stores.documents;
  }

  setUserCount(userCount: number): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, userCount });
  }

  incrementUserCount(): this {
    return this.setUserCount(this.userCount + 1);
  }

  toJSON(): Data {
    const { id, organizationId, name } = this;
    const company = this.company?.toJSON();
    const createdAt = this.createdAt?.toJSON();
    const updatedAt = this.updatedAt?.toJSON();
    return { id, organizationId, company, name, createdAt, updatedAt };
  }

  private getOrganizationIds(stores: StoreCollection): Set<string> {
    const a = new Set(this.id ? [this.id] : []);
    const b = stores.organizationIds;
    return new Set([...a, ...b]);
  }

  static sort(businesses: Franchisee[]) {
    function orderByOrder(a: Franchisee, b: Franchisee) {
      if (a.name.toString() < b.name.toString()) return -1;
      else if (a.name.toString() > b.name.toString()) return 1;
      else return 0;
    }
    return businesses.sort(orderByOrder);
  }

}

export { Franchisee };
export type {
  Data as FranchiseeData,
  DataToSave as FranchiseeDataToSave,
};

export * from './collection';
export * from './company-collection';